/**
 * HMRC claim routing for Claim tax back.
 *
 * The route depends on how the donor pays tax and the total amount GIVEN
 * (net of Gift Aid) in the tax year, per the routing table in the V1
 * requirements and https://www.gov.uk/donating-to-charity/gift-aid:
 *
 * - Fills in a tax return (including PAYE plus a return) -> charitable giving
 *   section of the return, any amount. HMRC will not take it by phone.
 * - PAYE, £5,000 or less given  -> phone HMRC.
 * - PAYE, over £5,000 given     -> in writing to HMRC.
 * - PAYE, £10,000 or more given -> in writing, listing date and charity for
 *   every donation.
 *
 * The figure given to HMRC is always the donation amount, NOT the Gift Aided
 * total: HMRC gross the figure up themselves.
 */

import type { ClaimRoute, ClaimTaxPayMethod } from '@/types/claimTaxBack'
import type { ClaimTaxYearConfig } from './taxYearConfig'

/** Tax return, or PAYE plus a tax return. Both use Self Assessment. */
export function usesSelfAssessmentRoute(
  payMethod: ClaimTaxPayMethod | null
): boolean {
  return payMethod === 'tax-return' || payMethod === 'both'
}

/** True once the donor has picked a route we can act on (not "not sure"). */
export function hasResolvedPayMethod(
  payMethod: ClaimTaxPayMethod | null
): boolean {
  return payMethod === 'paye' || usesSelfAssessmentRoute(payMethod)
}

/**
 * Resolves the HMRC route for a donor. Returns the "unknown" route while the
 * donor has not yet answered (or answered "not sure") so the UI can hold back
 * guidance until the answer exists.
 */
export function resolveClaimRoute(
  payMethod: ClaimTaxPayMethod | null,
  totalGivenPence: number,
  config: ClaimTaxYearConfig
): ClaimRoute {
  if (!Number.isInteger(totalGivenPence) || totalGivenPence < 0) {
    throw new Error(
      `Total given must be a non-negative integer in pence, got ${totalGivenPence}`
    )
  }

  if (payMethod === null || payMethod === 'not-sure') {
    return { id: 'unknown', requiresItemisedList: false }
  }

  // A tax return takes priority regardless of amount: HMRC will not accept a
  // phone claim from someone who files, including PAYE plus a return.
  if (usesSelfAssessmentRoute(payMethod)) {
    return { id: 'self-assessment', requiresItemisedList: false }
  }

  if (totalGivenPence >= config.payeItemisedFromGivenPence) {
    return { id: 'paye-writing-itemised', requiresItemisedList: true }
  }

  if (totalGivenPence > config.payePhoneMaxGivenPence) {
    return { id: 'paye-writing', requiresItemisedList: false }
  }

  return { id: 'paye-phone', requiresItemisedList: false }
}
